class AnimatedSlideshow extends HTMLElement {
  constructor() {
    super();
    this.slides = Array.from(this.querySelectorAll('.aslide'));
    if (this.slides.length === 0) return;

    this.viewport = this.querySelector('.animated-slideshow__viewport');
    this.prevButton = this.querySelector('[data-aslide-prev]');
    this.nextButton = this.querySelector('[data-aslide-next]');
    this.dots = Array.from(this.querySelectorAll('[data-aslide-dot]'));

    this.currentIndex = this.slides.findIndex((slide) => slide.classList.contains('is-active'));
    if (this.currentIndex < 0) this.currentIndex = 0;

    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.autoplay = this.dataset.autoplay === 'true' && this.slides.length > 1 && !this.reducedMotion;
    this.autoplaySpeed = parseInt(this.dataset.autoplaySpeed, 10) || 6000;
    this.pauseOnHover = this.dataset.pauseHover === 'true';

    if (this.prevButton) this.prevButton.addEventListener('click', () => this.step(-1));
    if (this.nextButton) this.nextButton.addEventListener('click', () => this.step(1));
    this.dots.forEach((dot) => {
      dot.addEventListener('click', () => this.goTo(parseInt(dot.dataset.aslideDot, 10)));
    });

    if (this.pauseOnHover) {
      this.addEventListener('mouseenter', () => this.stopAutoplay());
      this.addEventListener('mouseleave', () => this.startAutoplay());
      this.addEventListener('focusin', () => this.stopAutoplay());
      this.addEventListener('focusout', () => this.startAutoplay());
    }

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) this.stopAutoplay();
      else this.startAutoplay();
    });

    this.setupSwipe();
    this.playSlideAnimations(this.slides[this.currentIndex]);
    this.startAutoplay();
  }

  step(direction) {
    const next = (this.currentIndex + direction + this.slides.length) % this.slides.length;
    this.goTo(next);
  }

  goTo(index) {
    if (index === this.currentIndex || index < 0 || index >= this.slides.length) return;

    const previous = this.slides[this.currentIndex];
    const next = this.slides[index];

    this.slides.forEach((slide, i) => {
      slide.classList.remove('is-active', 'is-prev');
      if (i < index) slide.classList.add('is-prev');
    });
    next.classList.add('is-active');

    this.resetSlideAnimations(previous);
    this.playSlideAnimations(next);

    this.dots.forEach((dot) => {
      dot.classList.toggle('is-active', parseInt(dot.dataset.aslideDot, 10) === index);
    });

    this.currentIndex = index;
    this.restartAutoplay();
  }

  playSlideAnimations(slide) {
    if (!slide) return;
    slide.querySelectorAll('[data-anim]').forEach((el) => {
      el.classList.remove('is-playing');
      void el.offsetWidth;
      el.classList.add('is-playing');
    });
  }

  resetSlideAnimations(slide) {
    if (!slide) return;
    slide.querySelectorAll('[data-anim]').forEach((el) => {
      el.classList.remove('is-playing');
    });
  }

  startAutoplay() {
    if (!this.autoplay || this.autoplayTimer) return;
    this.autoplayTimer = setInterval(() => this.step(1), this.autoplaySpeed);
  }

  stopAutoplay() {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }

  restartAutoplay() {
    if (!this.autoplay) return;
    this.stopAutoplay();
    this.startAutoplay();
  }

  setupSwipe() {
    if (!this.viewport) return;
    let startX = 0;
    let startY = 0;
    let tracking = false;

    this.viewport.addEventListener('pointerdown', (event) => {
      startX = event.clientX;
      startY = event.clientY;
      tracking = true;
    });

    this.viewport.addEventListener('pointerup', (event) => {
      if (!tracking) return;
      tracking = false;
      const deltaX = event.clientX - startX;
      const deltaY = event.clientY - startY;
      if (Math.abs(deltaX) < 40 || Math.abs(deltaX) < Math.abs(deltaY)) return;
      this.step(deltaX < 0 ? 1 : -1);
    });

    this.viewport.addEventListener('pointercancel', () => {
      tracking = false;
    });
  }

  disconnectedCallback() {
    this.stopAutoplay();
  }
}

customElements.define('animated-slideshow', AnimatedSlideshow);
